import type { FC } from 'react'
import type { Theme } from '@mui/material'
import { Paper, Box, Typography, Chip } from '@mui/material'
import { ReviewRewardCard } from './ReviewRewardCard'

interface ReviewRewardItem {
  rewardId: string
  rewardName: string
  supportsEmployeeContribution: boolean
  companyContribution: number
  employeeContribution: number
}

interface ReviewCategoryCardProps {
  categoryName: string
  rewards: ReviewRewardItem[]
  theme: Theme
}

export const ReviewCategoryCard: FC<ReviewCategoryCardProps> = ({ categoryName, rewards, theme }) => {
  return (
    <Paper
      elevation={2}
      sx={{
        mb: 1.5,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Category Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[100],
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1, fontSize: '0.95rem' }}>
          {categoryName}
        </Typography>

        <Chip label={`${rewards.length}`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
      </Box>

      {/* Column Headers */}
      <Box
        sx={{
          px: 2.5,
          py: 0.75,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: theme.palette.mode === 'dark' ? '#5E5E5E' : theme.palette.grey[50],
        }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 120 }}>
          <Typography variant="caption" fontWeight={600} color="textSecondary">
            Reward
          </Typography>
        </Box>

        <Box sx={{ width: 140, textAlign: 'center' }}>
          <Typography variant="caption" fontWeight={600} color="textSecondary">
            Company
          </Typography>
        </Box>

        <Box sx={{ width: 140, textAlign: 'center' }}>
          <Typography variant="caption" fontWeight={600} color="textSecondary">
            Employee
          </Typography>
        </Box>
      </Box>

      {/* Rewards List */}
      <Box sx={{ p: 1 }}>
        {rewards.map((reward) => (
          <ReviewRewardCard
            key={reward.rewardId}
            rewardName={reward.rewardName}
            supportsEmployeeContribution={reward.supportsEmployeeContribution}
            companyContribution={reward.companyContribution}
            employeeContribution={reward.employeeContribution}
          />
        ))}
      </Box>
    </Paper>
  )
}
